<template>
  <v-overlay
    v-if="isExpired"
    absolute
    class="paywall-overlay"
    :class="{ 'drawer-open': drawerOpen && !isMobile }"
  >
    <div class="paywall-wrapper">
      <div class="paywall-card" :class="{ 'dark-mode': $vuetify.theme.dark }">
        <!-- Main content - stacked on mobile, side by side on desktop -->
        <div class="paywall-content">
          <!-- Image Section -->
          <div class="paywall-image-section">
            <img
              :src="displayImage"
              alt="Feature illustration"
              class="paywall-img"
            />
          </div>

          <!-- Text Section -->
          <div class="paywall-text-section">
            <div
              class="paywall-badge"
              :style="{ background: badgeBgColor, color: badgeTextColor }"
            >
              <v-icon small :color="badgeIconColor" class="mr-1">{{
                badgeIcon
              }}</v-icon>
              {{ displayBadge }}
            </div>

            <h1 class="paywall-title" v-html="formattedTitle"></h1>

            <p class="paywall-description">{{ displayDescription }}</p>

            <!-- Features List -->
            <div v-if="displayFeatures.length > 0" class="paywall-features">
              <div
                v-for="(feature, index) in displayFeatures"
                :key="index"
                class="feature-item"
              >
                <v-icon small color="success" class="mr-2"
                  >mdi-check-circle</v-icon
                >
                <span>{{ feature }}</span>
              </div>
            </div>

            <v-btn
              :color="buttonColor"
              dark
              large
              block
              depressed
              class="paywall-btn"
              @click="handleButtonClick"
            >
              <v-icon left small>{{ buttonIcon }}</v-icon>
              {{ displayButtonText }}
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </v-overlay>
</template>

<script>
import moment from "moment";

export default {
  props: {
    // Title customization
    title: {
      type: String,
      default: "",
    },
    // Description text
    description: {
      type: String,
      default: "",
    },
    // Badge text shown above title
    badge: {
      type: String,
      default: "",
    },
    // Badge icon (mdi icon name)
    badgeIcon: {
      type: String,
      default: "mdi-alert-circle",
    },
    // Badge colors
    badgeColor: {
      type: String,
      default: "warning", // warning, success, error, info, or hex color
    },
    // Features list
    features: {
      type: Array,
      default: () => [],
    },
    // Button text
    buttonText: {
      type: String,
      default: "",
    },
    // Button icon
    buttonIcon: {
      type: String,
      default: "mdi-credit-card-outline",
    },
    // Button color
    buttonColor: {
      type: String,
      default: "#4CAF50",
    },
    // Button route name (if using router)
    buttonRoute: {
      type: String,
      default: "Subscribe",
    },
    // Custom image path (optional)
    image: {
      type: String,
      default: "",
    },
    // Force show paywall (e.g., for feature-specific locks)
    forceShow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      drawerOpen: false,
    };
  },
  computed: {
    isExpired() {
      // Show paywall if forced OR if subscription expired
      if (this.forceShow) return true;
      const expiry = moment(this.$store.getters.companyDetails.plan.expiryDate);
      return moment().isAfter(expiry);
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    // Display values with defaults
    displayTitle() {
      return this.title || "Your Subscription\nHas Expired";
    },
    formattedTitle() {
      return this.displayTitle.replace(/\n/g, "<br/>");
    },
    displayDescription() {
      return (
        this.description ||
        "To continue using all premium features, please renew your subscription."
      );
    },
    displayBadge() {
      return this.badge || "Subscription Ended";
    },
    displayButtonText() {
      return this.buttonText || "Renew Subscription";
    },
    displayFeatures() {
      return this.features || [];
    },
    displayImage() {
      if (this.image) {
        try {
          return require(`@/assets/${this.image}`);
        } catch (e) {
          return require("@/assets/moneyTrial123.png");
        }
      }
      return require("@/assets/moneyTrial123.png");
    },
    // Badge styling based on color prop
    badgeBgColor() {
      const colorMap = {
        warning: "rgba(255, 152, 0, 0.12)",
        success: "rgba(76, 175, 80, 0.12)",
        error: "rgba(244, 67, 54, 0.12)",
        info: "rgba(33, 150, 243, 0.12)",
      };
      return colorMap[this.badgeColor] || `${this.badgeColor}20`;
    },
    badgeTextColor() {
      const colorMap = {
        warning: "#f57c00",
        success: "#388e3c",
        error: "#d32f2f",
        info: "#1976d2",
      };
      return colorMap[this.badgeColor] || this.badgeColor;
    },
    badgeIconColor() {
      return this.badgeColor;
    },
  },
  watch: {
    isExpired(newVal) {
      console.log("🔥 Emitting isExpired from PayWallCard.vue: ", newVal);
      this.$emit("update-is-expired", newVal);
      if (newVal) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },
  },
  async mounted() {
    await this.refreshCompanyDetails();

    console.log(
      "🔥 Mounted PayWallCard.vue - Checking Expiry:",
      this.isExpired,
    );
    this.$emit("update-is-expired", this.isExpired);
    if (this.isExpired) {
      document.body.style.overflow = "hidden";
    }

    // Listen for drawer state changes
    this.$root.$on("drawer-changed", (open) => {
      this.drawerOpen = open;
    });
  },
  beforeDestroy() {
    document.body.style.overflow = "";
    this.$root.$off("drawer-changed");
  },
  methods: {
    async refreshCompanyDetails() {
      try {
        await this.$store.dispatch(
          "getCompanyDetails",
          this.$store.getters.user,
        );
        console.log("Company details refreshed in PayWallCard");
      } catch (error) {
        console.error("Error refreshing company details:", error);
      }
    },
    handleButtonClick() {
      this.$emit("button-click");
      if (this.buttonRoute) {
        this.$router.push({ name: this.buttonRoute });
      }
    },
  },
};
</script>

<style scoped>
/* Overlay below app bar */
.paywall-overlay {
  position: fixed !important;
  top: 56px;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: calc(100% - 56px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7) !important;
  backdrop-filter: blur(8px);
  z-index: 4;
  padding: 16px;
  box-sizing: border-box;
  transition: left 0.2s ease, width 0.2s ease;
}

/* When drawer is open on desktop */
.paywall-overlay.drawer-open {
  left: 270px;
  width: calc(100% - 270px);
}

.paywall-wrapper {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
}

/* Card - Light mode (default) */
.paywall-card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

/* Card - Dark mode */
.paywall-card.dark-mode {
  background: #252525;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.paywall-content {
  display: flex;
  flex-direction: column;
}

/* Image Section */
.paywall-image-section {
  background: linear-gradient(135deg, #f8f9fc 0%, #eef1f5 100%);
  padding: 32px 24px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark-mode .paywall-image-section {
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
}

.paywall-img {
  width: 140px;
  height: auto;
  object-fit: contain;
}

/* Text Section */
.paywall-text-section {
  padding: 24px;
  text-align: center;
}

/* Badge */
.paywall-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 20px;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Title */
.paywall-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  line-height: 1.2;
}

.dark-mode .paywall-title {
  color: #ffffff;
}

/* Description */
.paywall-description {
  font-size: 15px;
  color: #666666;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.dark-mode .paywall-description {
  color: #a0a0a0;
}

/* Features List */
.paywall-features {
  text-align: left;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: rgba(76, 175, 80, 0.06);
  border-radius: 12px;
}

.dark-mode .paywall-features {
  background: rgba(76, 175, 80, 0.1);
}

.feature-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
  color: #333333;
}

.feature-item:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.dark-mode .feature-item {
  color: #cccccc;
}

.dark-mode .feature-item:not(:last-child) {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

/* Button */
.paywall-btn {
  border-radius: 12px !important;
  font-weight: 600;
  font-size: 15px !important;
  letter-spacing: 0.3px;
  text-transform: none;
  height: 52px !important;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.2s ease;
}

.paywall-btn:hover {
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4) !important;
  transform: translateY(-2px);
}

/* Tablet and above */
@media (min-width: 600px) {
  .paywall-wrapper {
    max-width: 480px;
  }

  .paywall-img {
    width: 160px;
  }

  .paywall-text-section {
    padding: 28px 32px 32px;
  }

  .paywall-title {
    font-size: 28px;
  }
}

/* Small mobile */
@media (max-width: 380px) {
  .paywall-overlay {
    padding: 12px;
  }

  .paywall-image-section {
    padding: 24px 16px 16px;
  }

  .paywall-img {
    width: 120px;
  }

  .paywall-text-section {
    padding: 20px 16px;
  }

  .paywall-title {
    font-size: 22px;
  }

  .paywall-description {
    font-size: 14px;
  }

  .paywall-btn {
    height: 48px !important;
    font-size: 14px !important;
  }
}
</style>
